import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import FieldWrapper from '../../../components/formInputs/FieldWrapper';
import SectionHeader from '../../../components/SectionHeader';
import Button from '../../../components/buttons/Button';
import { useMemo, useRef } from 'react';
import { scrollToError } from '../../../utils/HelperFunctions';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ITermsAndConditionsForm } from '../types/interfaces';
import useFormsIntegrationHelpers from '../../../hooks/useFormsIntegrationHelpers';
import FormDynamicSkeleton from '../../../components/loaders/FormDynamicSkeleton';
import { customStyles } from '../../../utils/SelectStyles';
import { TextEditorField } from '../../../components/formInputs/TextEditorField';

const CreateTermsAndConditionsForm = () => {
    const { id } = useParams();
    const formRef = useRef(null);
    const { t } = useTranslation();

    const { apiData, getDataLoading, handleSubmit, submitLoading } = useFormsIntegrationHelpers({
        queryKey: ['terms-and-conditions', id],
        invalidateQueryKey: ['terms-and-conditions'],
        id,
        singleGetApi: `/admin/cms/terms-and-conditions/${id}`,
        addApi: '/admin/cms/terms-and-conditions',
        editApi: `/admin/cms/terms-and-conditions/${id}`,
        listRoute: '/terms-and-conditions',
        itemName: t('terms-and-conditions')
    });

    const termsOptions = useMemo(() => {
        return [
            { label: t('terms-and-conditions'), value: "terms" },
            { label: t('order_terms'), value: "order-terms" },
            { label: t('financing_terms'), value: "financing-terms" }
        ];
    }, []);

    const validationSchema = Yup.object({
        type: Yup.string().required(t('required')),
        body: Yup.string().required(t('required')),
    });

    const initialValues: ITermsAndConditionsForm = {
        ...apiData,
        
        type: apiData?.terms_and_conditions?.type || '',
        body: apiData?.terms_and_conditions?.body || ''
    };

    if (getDataLoading)
        return (
            <FormDynamicSkeleton
                sections={[
                    [
                        { columns: 2, items: 1 },
                        { columns: 2, items: 1, type: 'textarea' }
                    ]
                ]}
                buttons={1}
            />
        );


    return (
        <div className={'form_section'} ref={formRef}>
            <Formik
                validateOnMount
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={(values, { setErrors }) => {
                    let value = { ...values, _method: 'POST' };
                    if (id) {
                        value = { ...value, _method: 'PUT' };
                    }
                    handleSubmit({ ...value }, setErrors);
                }}
            >
                {(formik) => (
                    <>
                        <Form>
                            <SectionHeader title={t('terms_and_conditions_details')} customStyle="last">
                                <div className="inputs_group">
                                    <FieldWrapper 
                                        customPadding 
                                        title={t('type')} 
                                        inputPlaceholder={t('type')} 
                                        inputName={`type`} 
                                        options={termsOptions} 
                                        selectStyle={customStyles} 
                                        select 
                                    />
                                    <FieldWrapper />
                                </div>
                                <div className="inputs_group custom_rich_text_input">
                                    <TextEditorField name="body" className="" />
                                </div>
                            </SectionHeader>
                            <div className="form_button reverse">
                                <Button
                                    loading={submitLoading}
                                    onClick={() => {
                                        scrollToError(!formik.isValid, formRef);
                                    }}
                                >
                                    <span className="bold">{t('save')}</span>
                                </Button>
                            </div>
                        </Form>
                    </>
                )}
            </Formik>
        </div>
    );
};

export default CreateTermsAndConditionsForm;
